import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BulkStepLayout from "../../pages/BulkStepLayout";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, IconButton, Backdrop, CircularProgress } from "@mui/material";
import { DashCircleFill } from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import { useItems } from "../../features/Fetch/Items";
import { BucketNodeRelationItem, BucketNodeRelationType, BucketNodeSnapshotResponse, BudgetItem, CurrentUser, FamilyBucketLabel, FamilyBucketPluralLabel, SortBucketNodesHierarchically } from "../../types";
import Description from "../Inputs/Description";
import Dropdown from "../Inputs/Dropdown";

interface BulkBucketNodeEditorProps {
  step: number;
  onNext: () => void;
  onBack: () => void;
  steps: string[];
  bucketTypeKey: string;
}

interface BucketNodeRow {
  rowKey: string;
  bucketNodeId?: number;
  displayName: string;
  originalDisplayName: string;
  parentBucketNodeId?: number | null;
  originalParentBucketNodeId?: number | null;
  isInactive: boolean;
  isViewTransactions: boolean;
}

const EMPTY_PARENT_ID = -1;

const BulkBucketNodeEditor = (props: BulkBucketNodeEditorProps) => {
  const { step, onNext, onBack, steps, bucketTypeKey } = props;
  const navigate = useNavigate();
  const repo = useItems();
  const budgets: BudgetItem[] = useSelector((state: any) => state.budgets.value);
  const snapshot: BucketNodeSnapshotResponse | undefined = useSelector((state: any) => state.bucketNodes.value);
  const user: CurrentUser = useSelector((state: any) => state.userData.value);
  const definition = (user.bucketDefinitions?.definitions ?? []).find(d => d.bucketTypeKey.toLowerCase() === bucketTypeKey.toLowerCase());
  const parentBucketTypeKey = definition?.parentBucketTypeKey;
  const bucketLabel = FamilyBucketLabel(user, bucketTypeKey, bucketTypeKey);
  const bucketPluralLabel = FamilyBucketPluralLabel(user, bucketTypeKey, `${bucketLabel}s`);
  const budgetPluralLabel = FamilyBucketPluralLabel(user, "budget", "Budgets");
  const parentBucketLabel = parentBucketTypeKey ? FamilyBucketLabel(user, parentBucketTypeKey, parentBucketTypeKey) : "";
  const nodes = SortBucketNodesHierarchically(
    (snapshot?.nodes ?? []).filter(node => node.bucketTypeKey.toLowerCase() === bucketTypeKey.toLowerCase()),
    snapshot?.relations);
  const parentNodes = parentBucketTypeKey
    ? (snapshot?.nodes ?? [])
      .filter(node => node.bucketTypeKey.toLowerCase() === parentBucketTypeKey.toLowerCase())
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
    : [];
  const childrenByParent = (snapshot?.relations ?? [])
    .filter((relation: BucketNodeRelationItem) => relation.relationType === BucketNodeRelationType.Hierarchy)
    .reduce((result: { [key: number]: number[] }, relation) => {
      if (!result[relation.sourceBucketNodeId]) result[relation.sourceBucketNodeId] = [];
      result[relation.sourceBucketNodeId].push(relation.targetBucketNodeId);
      return result;
    }, {} as { [key: number]: number[] });
  const parentByChild = (snapshot?.relations ?? [])
    .filter((relation: BucketNodeRelationItem) => relation.relationType === BucketNodeRelationType.Hierarchy)
    .reduce((result: { [key: number]: number }, relation) => {
      result[relation.targetBucketNodeId] = relation.sourceBucketNodeId;
      return result;
    }, {} as { [key: number]: number });
  const linkedBudgetCounts = budgets.reduce((result: { [key: number]: number }, budget) => {
    (budget.bucketNodes ?? [])
      .filter(node => node.bucketTypeKey.toLowerCase() === bucketTypeKey.toLowerCase())
      .forEach(node => {
        result[node.bucketNodeId] = (result[node.bucketNodeId] ?? 0) + 1;
      });
    return result;
  }, {} as { [key: number]: number });
  const version = [
    bucketTypeKey,
    parentBucketTypeKey ?? "",
    ...nodes.map(node => `${node.bucketNodeId}:${node.displayName}:${node.isInactive ? 1 : 0}:${node.isViewTransactions ? 1 : 0}:${parentByChild[node.bucketNodeId] ?? ""}`),
  ].join("|");
  const [rows, setRows] = useState<BucketNodeRow[]>([]);
  const [deletedRows, setDeletedRows] = useState<BucketNodeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (snapshot === undefined) {
      repo.GetBucketNodesAsync();
      return;
    }

    const nextRows = nodes.map(node => ({
      rowKey: node.bucketNodeId.toString(),
      bucketNodeId: node.bucketNodeId,
      displayName: node.displayName,
      originalDisplayName: node.displayName,
      parentBucketNodeId: parentByChild[node.bucketNodeId] ?? null,
      originalParentBucketNodeId: parentByChild[node.bucketNodeId] ?? null,
      isInactive: node.isInactive,
      isViewTransactions: node.isViewTransactions,
    }));

    setRows([...nextRows, createEmptyRow()]);
    setDeletedRows([]);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot, version]);

  if (loading) return <p>Loading...</p>;

  return (
    <BulkStepLayout step={step} onNext={onNext} onBack={onBack} steps={steps}>
      <Paper elevation={0} style={{ padding: 8, backgroundColor: 'azure', paddingBottom: 64 }}>
        <h2 className="bulk-category-title">Bulk {bucketPluralLabel} Editor</h2>
        <TableContainer>
          <Table size="small" className="fixed-width-table">
            <TableHead>
              <TableRow>
                <TableCell className="compact-table-cell header">{bucketLabel} Name</TableCell>
                {parentBucketTypeKey && (
                  <TableCell className="compact-table-cell header">{parentBucketLabel}</TableCell>
                )}
                <TableCell className="compact-table-cell header">{budgetPluralLabel}</TableCell>
                <TableCell className="compact-table-cell bottomless" style={{ width: 32 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => {
                const linkedBudgetCount = row.bucketNodeId ? linkedBudgetCounts[row.bucketNodeId] ?? 0 : 0;
                const hasChildren = row.bucketNodeId ? (childrenByParent[row.bucketNodeId]?.length ?? 0) > 0 : false;

                return (
                  <TableRow key={row.rowKey} sx={{ backgroundColor: idx % 2 === 0 ? '#ddffff' : '#ffffff' }}>
                    <TableCell className="compact-table-cell">
                      <Description
                        NoLabel
                        NoMB
                        Variant="standard"
                        Value={row.displayName}
                        OnChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCellChange(idx, { displayName: e.target.value })}
                        Size={12}
                      />
                    </TableCell>
                    {parentBucketTypeKey && (
                      <TableCell className="compact-table-cell">
                        <Dropdown
                          HideLabel
                          NoMB
                          Variant="standard"
                          Options={[
                            { Id: EMPTY_PARENT_ID, Value: "" },
                            ...parentNodes.map(node => ({ Id: node.bucketNodeId, Value: node.displayName }))
                          ]}
                          Value={row.parentBucketNodeId ?? EMPTY_PARENT_ID}
                          OnChange={(value: number) => handleCellChange(idx, { parentBucketNodeId: value <= EMPTY_PARENT_ID ? null : value })}
                          Size={12}
                          EnableNegative
                        />
                      </TableCell>
                    )}
                    <TableCell className="compact-table-cell px-10">
                      {linkedBudgetCount > 0 ? linkedBudgetCount : ""}
                    </TableCell>
                    <TableCell className="compact-table-cell bottomless" style={{ width: 32, backgroundColor: 'azure' }}>
                      {rows.length > 1 && idx !== rows.length - 1 && (
                        <IconButton
                          size="small"
                          color="error"
                          tabIndex={-1}
                          disabled={linkedBudgetCount > 0 || hasChildren}
                          onClick={() => handleDeleteRow(idx)}>
                          <DashCircleFill />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <div className="bulk-budget-action-row">
          <Button
            variant="outlined"
            color="error"
            onClick={onBack}
            style={{ minWidth: 160, padding: '4px 16px' }}>
            Back
          </Button>
          <div className="bulk-budget-action-buttons">
            {hasChangesToSave() && (
              <Button
                variant="contained"
                color="success"
                onClick={handleSaveAndExit}
                style={{ minWidth: 120, padding: '4px 16px' }}>
                Save and Exit
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={hasChangesToSave() ? handleSaveAndContinue : onNext}
              style={{ minWidth: 120, padding: '4px 16px' }}>
              {hasChangesToSave() ? 'Save and Continue' : 'Continue'}
            </Button>
          </div>
        </div>
        <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading || saving}>
          <CircularProgress />
        </Backdrop>
      </Paper>
    </BulkStepLayout>
  );

  function createEmptyRow(): BucketNodeRow {
    return {
      rowKey: `new-${Date.now()}-${Math.random()}`,
      displayName: "",
      originalDisplayName: "",
      parentBucketNodeId: null,
      originalParentBucketNodeId: null,
      isInactive: false,
      isViewTransactions: true,
    };
  }

  function handleCellChange(index: number, changes: Partial<BucketNodeRow>) {
    const updatedRows = rows.map((row, rowIndex) => rowIndex === index ? { ...row, ...changes } : row);
    const currentRow = updatedRows[index];
    const isLastRow = index === updatedRows.length - 1;
    const hasStartedRow = !!currentRow.displayName.trim() || currentRow.parentBucketNodeId != null;

    if (isLastRow && hasStartedRow) {
      updatedRows.push(createEmptyRow());
    }

    setRows(updatedRows);
  }

  function handleDeleteRow(index: number) {
    const row = rows[index];
    if (row.bucketNodeId) {
      setDeletedRows(prev => [...prev, row]);
    }

    setRows(rows.filter((_, rowIndex) => rowIndex !== index));
  }

  function hasChangesToSave() {
    return deletedRows.length > 0 || rows.slice(0, -1).some(hasChanged);
  }

  function hasChanged(row: BucketNodeRow) {
    if (!row.bucketNodeId) return !!row.displayName.trim();

    return row.displayName.trim() !== row.originalDisplayName.trim() || normalizeParentId(row.parentBucketNodeId) !== normalizeParentId(row.originalParentBucketNodeId);
  }

  async function handleSave() {
    setSaving(true);

    await Promise.all(
      deletedRows
        .filter(row => row.bucketNodeId)
        .map(row => repo.DeleteCustomBucketNodeAsync(row.bucketNodeId!))
    );

    await Promise.all(
      rows
        .slice(0, -1)
        .filter(row => row.bucketNodeId && row.displayName.trim() && hasChanged(row))
        .map(row => repo.UpdateCustomBucketNodeAsync({
          bucketNodeId: row.bucketNodeId,
          bucketTypeKey,
          displayName: row.displayName.trim(),
          isInactive: row.isInactive,
          isViewTransactions: row.isViewTransactions,
          parentBucketNodeId: parentBucketTypeKey ? normalizeParentId(row.parentBucketNodeId) : undefined,
        }))
    );

    await Promise.all(
      rows
        .slice(0, -1)
        .filter(row => !row.bucketNodeId && row.displayName.trim())
        .map(row => repo.AddCustomBucketNodeAsync({
          bucketTypeKey,
          displayName: row.displayName.trim(),
          isInactive: false,
          isViewTransactions: true,
          parentBucketNodeId: parentBucketTypeKey ? normalizeParentId(row.parentBucketNodeId) : undefined,
        }))
    );

    await repo.GetBucketNodesAsync();
    setSaving(false);
  }

  async function handleSaveAndContinue() {
    await handleSave();
    onNext();
  }

  async function handleSaveAndExit() {
    await handleSave();
    navigate('/budgets');
  }

  function normalizeParentId(parentId?: number | null) {
    return parentId ?? null;
  }
};

export default BulkBucketNodeEditor;